import { useCallback } from "react";
import { useDispatch, useStore } from "react-redux";
import { RootState, useAppSelector } from "src/store";
import { useCampaignsApi } from "../api";
import { resetError, resetList, setError, setFilter, setList, setLoading } from "../slices";

const getServerSortField = (sort?: string) => {
  if (!sort) return undefined;

  const sortMap: Record<string, string> = {
    createdAt: "date_created",
    updatedAt: "date_updated",
  };

  const [field, order] = sort.split("_");
  const serverField = sortMap[field] || field;
  return `${serverField}_${order}`;
};

export const useCampaignsService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<RootState>();
  const { loading, filter, list, error } = useAppSelector((state) => state.campaigns);

  const api = useCampaignsApi();

  const getCampaigns = useCallback(async (companyId: string) => {
    const filter = getState().campaigns.filter;

    try {
      dispatch(resetError());
      dispatch(setLoading({ getList: true }));

      const response = await api.getCampaigns({
        companyId,
        type: filter.configId === 'all' || !filter.configId ? '' : undefined,
        limit: filter.limit,
        offset: filter.offset,

        query: filter.query || undefined,
        sort: getServerSortField(filter.sort),
        status: filter.status === 'all' || !filter.status ? undefined : filter.status,
        configId: filter.configId === 'all' || !filter.configId ? undefined : filter.configId,
        appointmentTypeId: filter.appointmentTypeId,
        date_created: filter.date_created,
        date_updated: filter.date_updated,
      });
      dispatch(setList(response.data));

      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getList: false }));
    }
  }, []);

  const changeCampaignsFilter = useCallback((filter: Partial<RootState["campaigns"]["filter"]>) => {
    dispatch(setFilter(filter));
  }, []);

  const resetCampaigns = useCallback(() => {
    dispatch(resetList());
  }, []);

  return {
    loading,
    filter,
    list,
    error,
    getCampaigns,
    changeCampaignsFilter,
    resetCampaigns,
  };
};
